import { useEffect } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import SectionHeading from "./SectionHeading";

const FAQS = [
  {
    q: "Do I have to buy shakes, powders or supplement packages?",
    a: "No. Nikhil does not sell, stock or earn commission on any nutrition product, and there are no commercial meal replacement shakes, synthetic MLM powders or blind fat burners in this coaching. The approach is whole-food first: sprouts, bhakri, paneer, curd, eggs, seasonal vegetables and good fats like ghee and cold-pressed oils. If your deep medical history or blood test markers genuinely show a gap — vitamin D, B12, iron, thyroid-related support — a targeted, clinical supplement may be recommended and reviewed with your doctor. Evidence first, never a product push.",
  },
  {
    q: "Is this a keto or low-carb diet?",
    a: "It is a low glycaemic index, zero-added-sugar protocol — not textbook keto. Carbohydrates are not banned, they are chosen carefully: bhakri instead of chapati, quinoa or moong chilla at night, berries instead of banana. The goal is stable blood sugar and steady energy, not carb elimination.",
  },
  {
    q: "What exactly do I get as a client?",
    a: "A plan built from your own assessment — medical history, thyroid/PCOS/sugar status, work hours, kitchen and food preferences. It carries exact portions in grams, meal timings, food swaps and a lifestyle protocol. You then send meal photos on WhatsApp daily, get corrections the same day, and your plan is re-tuned at a full checkpoint review every 15 days.",
  },
  {
    q: "Is bhakri, curd, ghee or peanut butter really allowed?",
    a: "Yes. Ghee, butter, cold-pressed coconut oil, curd, paneer and a spoon of peanut butter are part of the protocol. What gets removed is refined oil, added sugar, packaged food, hybrid vegetables and high-GI staples like rice, wheat, potato and banana during the fat-loss phase.",
  },
  {
    q: "Will the weight come back once I stop?",
    a: "The plan is deliberately built from food you already cook at home, so there is nothing to 'come off'. Towards the end of the fat-loss phase, foods are reintroduced in a controlled way and you are taught the rules behind the choices — portion logic, meal timing, low-GI swaps — so you can hold the result without a coach watching.",
  },
  {
    q: "I have thyroid, PCOS or diabetes. Can I still join?",
    a: "Usually yes, and many clients come in precisely for these reasons. Declare your condition and medication in the application form. High-risk cases — insulin-dependent diabetes, pregnancy or breastfeeding, kidney, liver or heart conditions — are screened first and the plan is kept conservative and must be cleared by your treating doctor.",
  },
  {
    q: "What about eating out, travel and family functions?",
    a: "They are planned for, not punished. You will get ordering rules for restaurants, a travel-day structure and a simple recovery protocol for functions and festivals. Send the photo, get the correction, move on — no guilt cycles.",
  },
  {
    q: "What are Nikhil's qualifications?",
    a: "Nikhil Shrirao is a Certified Dietitian & Wellness Coach certified by the National Health Care Academy, with 7 years of practice and 200+ clients coached one-on-one. He is a nutrition coach, not a doctor — plans are guidance and never replace your physician's advice.",
  },
];

export default function Faq() {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <section id="faq" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <SectionHeading
          eyebrow="Straight Answers"
          title="Questions Clients Actually Ask"
          sub="No sales script. If this coaching is not right for you, you will be told so before you pay anything."
        />
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7 }}
          className="mt-14 grid lg:grid-cols-12 gap-6"
        >
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`faq-${i}`}
                  className="rounded-2xl border border-line bg-panel px-6 data-[state=open]:border-accent/40 transition-colors"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="py-5 text-left font-display text-lg font-bold uppercase tracking-wide text-white hover:no-underline hover:text-emerald-300">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-sm text-slate-400 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <div className="lg:col-span-4 rounded-3xl border border-gold/30 bg-gradient-to-br from-panel to-ink p-8 h-fit">
            <Plus className="h-6 w-6 text-gold" />
            <p className="mt-5 font-display text-xl font-bold uppercase text-white">Still unsure?</p>
            <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">
              Send your question on WhatsApp. You will get an honest answer from Nikhil himself — not a sales team.
            </p>
            <a
              href="#intake-form"
              data-testid="faq-apply-cta"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-extrabold uppercase tracking-wide text-ink transition-all duration-300 hover:shadow-glow-gold hover:-translate-y-0.5"
            >
              Apply For Coaching
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
